import {
  birthYearRange,
  changePasswordRequestSchema,
  meResponseSchema,
  updateAccountRequestSchema,
  type ChangePasswordRequest,
  type MeResponse,
} from '@koderek/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Badge, Button, Group, List, Paper, PasswordInput, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { api } from '../api/client';
import { ErrorAlert } from '../components/ErrorAlert';
import { errorMessage, useMe, useSetMe } from '../features/auth/session';
import { editionLabel, formatDate } from '../features/editions/format';
import { usePastEditions } from '../features/editions/queries';
import { t } from '../texts/pl';

// „Jestem uczniem” tylko w formularzu – do API idzie rok urodzenia albo null (PRD §10 pkt 61).
const accountFormSchema = updateAccountRequestSchema
  .extend({ student: z.boolean() })
  .refine((v) => !v.student || typeof v.birthYear === 'number', { path: ['birthYear'], message: t.account.birthYearRequired });
type AccountForm = z.input<typeof accountFormSchema>;
type AccountValues = z.output<typeof accountFormSchema>;

/** US-208 dane konta, US-207 zmiana hasła, US-104 AC-5 poprzednie edycje. */
export function AccountPage() {
  const me = useMe().data;
  if (!me) return null;
  return (
    <Stack gap="lg" maw={640}>
      <Title order={1}>{t.account.title}</Title>
      <Button component={Link} to="/rodzina" variant="light" w="fit-content">
        {t.nav.family}
      </Button>
      <AccountData me={me} />
      <ChangePassword />
      <PastEditions />
    </Stack>
  );
}

function AccountData({ me }: { me: MeResponse }) {
  const setMe = useSetMe();
  const form = useForm<AccountForm, unknown, AccountValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      firstName: me.firstName,
      lastName: me.lastName,
      school: me.school ?? '',
      referralSource: me.referralSource ?? '',
      student: me.birthYear !== null,
      birthYear: me.birthYear ?? '',
    },
  });
  const student = form.watch('student');
  const range = birthYearRange(new Date().getFullYear());
  const save = useMutation({
    mutationFn: ({ student: isStudent, birthYear, ...rest }: AccountValues) =>
      api('PATCH', '/account', { body: { ...rest, birthYear: isStudent ? birthYear : null }, schema: meResponseSchema }),
    onSuccess: setMe,
  });

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <Stack gap="sm">
          <Title order={2} size="h3">
            {t.account.dataTitle}
          </Title>
          <TextInput label={t.fields.email} value={me.email} readOnly description={t.account.emailInfo} />
          {me.roles.length ? (
            <Group gap="xs">
              <Text size="sm">{t.account.roles}:</Text>
              {me.roles.map((role) => (
                <Badge key={role} variant="light">
                  {t.roles[role]}
                </Badge>
              ))}
            </Group>
          ) : null}
          <TextInput label={t.fields.firstName} required {...form.register('firstName')} error={form.formState.errors.firstName?.message} />
          <TextInput label={t.fields.lastName} required {...form.register('lastName')} error={form.formState.errors.lastName?.message} />
          <Switch label={t.account.student} description={t.account.studentHint} {...form.register('student')} />
          {student ? (
            <TextInput
              label={t.participants.birthYear}
              description={t.participants.birthYearHint(range.min, range.max)}
              type="number"
              inputMode="numeric"
              min={range.min}
              max={range.max}
              required
              {...form.register('birthYear')}
              error={form.formState.errors.birthYear?.message}
            />
          ) : null}
          <TextInput label={t.fields.school} {...form.register('school')} error={form.formState.errors.school?.message} />
          <TextInput label={t.fields.referralSource} {...form.register('referralSource')} error={form.formState.errors.referralSource?.message} />
          {save.isError ? <ErrorAlert error={save.error} /> : null}
          {save.isSuccess ? <Alert color="green" role="status">{t.account.saved}</Alert> : null}
          <Button type="submit" loading={save.isPending} w="fit-content">
            {t.account.save}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

// Powtórzenie nowego hasła tylko w formularzu – do API idą obecne i nowe hasło.
const changePasswordFormSchema = changePasswordRequestSchema
  .extend({ newPasswordRepeat: z.string() })
  .refine((v) => v.newPassword === v.newPasswordRepeat, { path: ['newPasswordRepeat'], message: t.fields.passwordMismatch });
type ChangePasswordForm = z.infer<typeof changePasswordFormSchema>;

function ChangePassword() {
  const form = useForm<ChangePasswordForm>({ resolver: zodResolver(changePasswordFormSchema) });
  const change = useMutation({
    mutationFn: (body: ChangePasswordRequest) => api('POST', '/account/password', { body }),
    onSuccess: () => form.reset({ currentPassword: '', newPassword: '', newPasswordRepeat: '' }),
  });

  return (
    <Paper withBorder p="md" radius="md">
      <form
        onSubmit={form.handleSubmit(({ currentPassword, newPassword }) => change.mutate({ currentPassword, newPassword }))}
        noValidate
      >
        <Stack gap="sm">
          <Title order={2} size="h3">
            {t.account.passwordTitle}
          </Title>
          <PasswordInput label={t.fields.currentPassword} autoComplete="current-password" required {...form.register('currentPassword')} error={form.formState.errors.currentPassword?.message} />
          <PasswordInput
            label={t.fields.newPassword}
            description={t.fields.passwordHint}
            autoComplete="new-password"
            required
            {...form.register('newPassword')}
            error={form.formState.errors.newPassword?.message}
          />
          <PasswordInput
            label={t.fields.passwordRepeat}
            autoComplete="new-password"
            required
            {...form.register('newPasswordRepeat')}
            error={form.formState.errors.newPasswordRepeat?.message}
          />
          {change.isError ? <Alert color="red" role="alert">{errorMessage(change.error)}</Alert> : null}
          {change.isSuccess ? <Alert color="green" role="status">{t.account.passwordChanged}</Alert> : null}
          <Button type="submit" loading={change.isPending} w="fit-content">
            {t.account.changePassword}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

function PastEditions() {
  const past = usePastEditions();
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={2} size="h3">
          {t.editions.past}
        </Title>
        {past.isError ? <Alert color="red">{errorMessage(past.error)}</Alert> : null}
        {past.data && !past.data.editions.length ? <Text c="dimmed">{t.editions.pastEmpty}</Text> : null}
        {past.data?.editions.length ? (
          <List>
            {past.data.editions.map((e) => (
              <List.Item key={e.id}>
                <Anchor component={Link} to={`/edycje/${e.slug}`}>
                  {editionLabel(e)}
                </Anchor>
                , {formatDate(e.date)}
              </List.Item>
            ))}
          </List>
        ) : null}
      </Stack>
    </Paper>
  );
}
